import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useApp from '../hooks/useApp';
import useUI from '../hooks/useUI';
import useData from '../hooks/useData';
import './leaflet.css';
import markerSearch from '../assets/marker-search.svg';
import markerFav from '../assets/marker-fav.svg';
// import markerStartCHT from '../assets/start-cht.svg';
// import markerStartENG from '../assets/start-eng.svg';
import markerStart from '../assets/marker-start.svg';
// import markerEnd from '../assets/marker-end.svg'
import { MapContainer, ImageOverlay, Marker, Rectangle, Polygon, Popup, Pane, useMapEvents, Polyline } from 'react-leaflet'
import { CRS, latLngBounds, divIcon, icon } from 'leaflet';
import { useWindowSize } from 'react-use';
import useRecord from '../hooks/useRecord';


const FeatureWithText = ({ map, shape, positions, bounds, exhibitor, colors }) => {
	const { makeRecord } = useRecord();
	const recordExhibitorClicked = useCallback((booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
		});
	}, [makeRecord]);

	const { width } = useWindowSize();

	const minZoom = useMemo(() => {
		if (width <= 1440) return 1;
		return 2;
	}, [width]);

	const [currentZoom, setCurrentZoom] = useState(minZoom);
	const { selectedBooth, setSelectedExhibitor } = useApp();
	const { toggleExhibitorInfo } = useUI();
	const { exhibitorInfo } = useData();

	const mapEvents = useMapEvents({
		zoomend: () => {
			setCurrentZoom(mapEvents.getZoom());
		},
	});

	const { isCHT } = useApp();

	const exhibitorName = useMemo(() => {
		if (isCHT) return exhibitor?.['廠商簡稱-中文'] || '';
		return exhibitor?.['廠商簡稱-英文'] || '';
	}, [exhibitor, isCHT]);

	const featureWidthPixels = useMemo(() => {
		if (map) {
			const pixelPosition = map.project([bounds[0][0], bounds[0][1]], currentZoom);
			const pixelPosition2 = map.project([bounds[1][0], bounds[1][1]], currentZoom);
			return `${(pixelPosition2.x - pixelPosition.x).toString().replace('-', '')}px`;
		}
		return '0';
	}, [bounds, currentZoom, map]);

	const featureHeightPixels = useMemo(() => {
		if (map) {
			const pixelPosition = map.project([bounds[0][0], bounds[0][1]], currentZoom);
			const pixelPosition2 = map.project([bounds[1][0], bounds[1][1]], currentZoom);
			return `${(pixelPosition2.y - pixelPosition.y).toString().replace('-', '')}px`;
		}
		return '0';
	}, [bounds, currentZoom, map]);

	const getTextBits = useCallback(() => {
		let charactersPerLine = 8;

		const textBits = [];


		const exhibitorNameLines = exhibitorName.split(' ');
		let currentLine = '';
		exhibitorNameLines.forEach((word) => {
			if (currentLine.length + word.length > charactersPerLine) {
				textBits.push(currentLine);
				currentLine = '';
			}
			currentLine += ` ${word}`;
		});
		textBits.push(currentLine);

		textBits.forEach((text, index) => {
			textBits[index] = text.trim();
		});

		return textBits;
	}, [exhibitorName]);

	const textIcon = useMemo(() => {
		const width = featureWidthPixels?.replace('px', '');
		const height = featureHeightPixels?.replace('px', '');
		const numWidth = Number(width);
		const numHeight = Number(height);
		const paddingTopBot = currentZoom * 1;
		const paddingLeftRight = currentZoom * 2;
		const canvas = document.createElement('canvas');

		canvas.width = numWidth;
		canvas.height = numHeight;

		const ctx = canvas.getContext('2d');

		let boothFontSize = currentZoom * 8;
		if (numHeight / boothFontSize < 2.5) boothFontSize = numHeight / 2.5;
		if (numWidth / boothFontSize < 2.5) boothFontSize = numWidth / 3;
		ctx.globalAlpha = (100 - Number(colors.textOpacity)) / 100;
		ctx.font = `${boothFontSize}px Noto Sans TC`;
		ctx.fillText(exhibitor?.['攤位號碼'], paddingLeftRight, boothFontSize + paddingTopBot, numWidth - paddingLeftRight * 2);

		if (isCHT) {
			const avaialbleHeight = numHeight - (boothFontSize + paddingTopBot * 2);
			const avaialbleWidth = numWidth - paddingLeftRight * 2;
			const characterCount = exhibitorName.length;
			const characterWidth = avaialbleWidth / characterCount;

			let nameFontSize = boothFontSize;

			if (characterWidth < avaialbleHeight) nameFontSize = characterWidth;

			ctx.font = `${nameFontSize}px Noto Sans TC`;

			ctx.fillText(exhibitorName, paddingLeftRight, (boothFontSize + paddingTopBot) + (nameFontSize), numWidth - paddingLeftRight * 2);

		} else {

			let nameFontSize = currentZoom * 8;
			if (numHeight / nameFontSize < 2.5) nameFontSize = numHeight / 2.5;

			ctx.font = `${nameFontSize}px Noto Sans TC`;

			const textBits = getTextBits();

			textBits.forEach((text, index) => {
				ctx.fillText(text, paddingLeftRight, (boothFontSize + paddingTopBot) + (nameFontSize * (index + 1)), numWidth - paddingLeftRight * 2);
			});
		}

		const dataUrl = canvas.toDataURL();
		const html = `<img src='${dataUrl}' />`;

		return divIcon({
			html,
			className: 'booth-text',
			iconSize: [Number(width), Number(height)],
		});
	}, [colors.textOpacity, currentZoom, exhibitor, exhibitorName, featureHeightPixels, featureWidthPixels, getTextBits, isCHT]);

	const rectRef = useRef(null);

	const Feature = useMemo(() => {
		if (shape === 'rectangle') return Rectangle;
		if (shape === 'polygon') return Polygon;
	}, [shape]);

	const isSelected = useMemo(() => {
		return selectedBooth === exhibitor?.['攤位號碼'];
	}, [exhibitor, selectedBooth]);

	const isFavourite = useMemo(() => {
		if (isSelected) {
			const exhibitorInfoItem = exhibitorInfo.find(item => item['攤位號碼'] === exhibitor?.['攤位號碼']);
			if (exhibitorInfoItem) {
				return exhibitorInfoItem.favourite;
			}
		}
		return false;
	}, [exhibitor, exhibitorInfo, isSelected]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const center = rectRef.current?.getCenter() || [0, 0];

	const markerIcon = useMemo(() => {
		const size = currentZoom * 20;
		return icon({
			iconUrl: isFavourite ? markerFav : markerSearch,
			iconSize: [size, size],
		});
	}, [currentZoom, isFavourite]);

	const markerCenter = useMemo(() => {
		const offset = currentZoom * 5;
		if (map) {
			const pixelCenter = map.project(center, currentZoom);
			pixelCenter.y -= offset;

			const latLngCenter = map.unproject(pixelCenter, currentZoom);
			return latLngCenter;
		}

		return [0, 0];
	}, [center, currentZoom, map]);

	const chooseExhibitor = useCallback(() => {
		if (exhibitor) {
			setSelectedExhibitor(exhibitor['攤位號碼']);
			toggleExhibitorInfo();
			recordExhibitorClicked(exhibitor['攤位號碼']);
		}
	}, [exhibitor, recordExhibitorClicked, setSelectedExhibitor, toggleExhibitorInfo]);

	const isTouchDevice = useMemo(() => {
		return 'ontouchstart' in window || navigator.maxTouchPoints;
	}, []);
	const popup = useRef(null);

	return (
		<Feature
			ref={rectRef}
			bounds={bounds}
			positions={positions}
			pathOptions={{
				color: colors.border,
				fillColor: colors.fill,
				fillOpacity: (100 - Number(colors.fillOpacity)) / 100,
				opacity: (100 - Number(colors.borderOpacity)) / 100,
				weight: 3,
			}}
		>
			{exhibitor ? (
				<Pane name={`marker${exhibitor?.['攤位號碼']}`} style={{ zIndex: 400 }}>
					<Marker position={center} icon={textIcon} eventHandlers={{
						mouseover: () => {
							if (isTouchDevice) return;
							rectRef.current.fire('click');
						},
						click: () => {
							if (!isTouchDevice) return;
							rectRef.current.fire('click');
						},
						// mouseout: () => {
						// 	popup.current._closeButton.click();
						// }
					}} />
				</Pane>
			) : null}

			{isSelected ? (
				<Pane name='marker' style={{ zIndex: 400 }}>
					<Marker position={markerCenter} icon={markerIcon}>
					</Marker>
				</Pane>
			) : null}

			{exhibitor ? (
				<Popup ref={popup}>
					<div className='booth-info-popup' onClick={chooseExhibitor}>
						<div className='title'>{exhibitorName}</div>
						<div className='info-wrap'>
							<div className='details'>
								{isCHT ? exhibitor?.['展商基本介紹-中文'] || '' : exhibitor?.['展商基本介紹-英文'] || ''}
							</div>
						</div>
					</div>
				</Popup>
			) : null}
		</Feature>
	)
}

const NavMarker = ({ center, marker }) => {
	const [currentZoom, setCurrentZoom] = useState();

	const mapEvents = useMapEvents({
		zoomend: () => {
			setCurrentZoom(mapEvents.getZoom());
		},
	});

	const markerSize = useMemo(() => {
		const size = currentZoom * 12;
		return [size, size];
	}, [currentZoom]);

	const markerIcon = useMemo(() => {
		return icon({
			iconUrl: marker,
			iconSize: markerSize,
		});
	}, [marker, markerSize]);

	// const markerRef = useRef(null);

	// useEffect(() => {
	// 	if (markerRef.current) {
	// 		markerRef.current.zIndex = 250;
	// 		console.log(markerRef.current.zIndex = 250);
	// 	}
	// }, [markerRef]);

	return (
		<Marker position={{
			lat: center[0],
			lng: center[1],
		}} icon={markerIcon} />
	)
}

const MapArea = ({ isPrint, floor }) => {
	const { activeFloor, selectedBooth, language, setFloorAndLang, setLanguage, navigation } = useApp();
	const { basicInfo, floorInfo, setDataFetched } = useData();

	const currentFloor = useMemo(() => {
		if (floor) return floor;
		return floorInfo ? floorInfo[activeFloor] : null;
	}, [activeFloor, floor, floorInfo]);

	const map = useRef(null);

	const currentMap = useMemo(() => {
		if (!currentFloor) return {};
		return currentFloor[`map${language}`];
	}, [currentFloor, language]);

	const bounds = useMemo(() => {
		if (!currentMap.width || !map.current?.unproject) {
			return null;
		}

		const A4WidthInPixels = 2200;
		const height = (currentMap.height / currentMap.width) * A4WidthInPixels;
		const southWest = map.current.unproject([0, height], 2.5);
		const northEast = map.current.unproject([A4WidthInPixels, 0], 2.5);
		const bounds = new latLngBounds(southWest, northEast);
		// add padding to bounds
		// console.log(bounds);
		// bounds._northEast.lat += 5;
		// bounds._northEast.lng += 5;
		// bounds._southWest.lat -= 5;
		// bounds._southWest.lng -= 5;

		return bounds;
	}, [currentMap]);

	useEffect(() => {
		if (map.current?.fitBounds && bounds && !selectedBooth) {
			map.current.fitBounds(bounds);
			setTimeout(() => {
				map.current.setMaxBounds(bounds);
			}, 300);
		}
	}, [bounds, selectedBooth]);

	const currentFloorBooths = useMemo(() => {
		return currentFloor?.booths || [];
	}, [currentFloor?.booths]);

	const currentFloorSections = useMemo(() => {
		return currentFloor?.sections || [];
	}, [currentFloor?.sections]);

	const { width } = useWindowSize();

	const minZoom = useMemo(() => {
		if (width <= 1440) return 1;
		return 2;
	}, [width]);

	const [startPoint, setStartPoint] = useState(null);
	const [endPoint, setEndPoint] = useState(null);

	useEffect(() => {
		if (selectedBooth) {
			const booth = currentFloorBooths.find((booth) => booth.linked === selectedBooth);
			if (booth) {
				// map.current.setZoom(3);

				const bounds = [
					[booth.bounds._northEast.lat, booth.bounds._northEast.lng],
					[booth.bounds._southWest.lat, booth.bounds._southWest.lng],
				];

				const center = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];

				if (navigation && startPoint) {
					// map.current.setZoom(minZoom + 1).panTo(startPoint);
					map.current?.setView(startPoint, minZoom + 1, true);
					setTimeout(() => {
						map.current?.setView(startPoint, minZoom);
					}, 500);
				}

				if (!navigation) {
					// map.current.once('moveend', () => {
					// 	console.log('moveend');
					// });
					// map.current.setZoom(3).panTo(center);
					map.current?.setView(center, 3);
					// setTimeout(() => {
					// 	map.current.panTo(center);
					// }, 500);
				}

			}
		}
	}, [currentFloorBooths, selectedBooth, navigation, minZoom, startPoint]);

	useEffect(() => {
		if (isPrint && !floor) {
			const urlParams = new URLSearchParams(window.location.search);
			const floor = urlParams.get('floor');
			const lang = urlParams.get('lang');

			if (floor && lang) {
				setFloorAndLang(floor, lang);
			}
		}
	}, [floor, isPrint, setFloorAndLang]);

	useEffect(() => {
		if (isPrint && !floor) {
			setTimeout(() => {
				window.print();
			}, 2000);
		}
	}, [floor, isPrint]);

	useEffect(() => {
		if (floor) {
			const urlParams = new URLSearchParams(window.location.search);
			const lang = urlParams.get('lang');
			setLanguage(lang);
		}
	}, [floor, setLanguage]);

	useEffect(() => {
		if (isPrint) {
			setDataFetched(false);
		}
	}, [isPrint, setDataFetched]);

	const [navigationPath, setNavigationPath] = useState(null);
	// const [startIconPosition, setStartIconPosition] = useState(null);


	const roundedPaths = useMemo(() => {
		return currentFloor?.paths.map((path) => {
			const roundedPositions = path.positions.map((position) => {
				return [Math.round(position[0]), Math.round(position[1])];
			});

			const diff1 = roundedPositions[0][0] - roundedPositions[1][0];
			const diff2 = roundedPositions[0][1] - roundedPositions[1][1];
			const leastDiff = Math.abs(diff1) < Math.abs(diff2) ? diff1 : diff2;
			const direction = leastDiff === diff1 ? 'horizontal' : 'vertical';

			if (direction === 'horizontal') roundedPositions[1][0] = roundedPositions[0][0];
			if (direction === 'vertical') roundedPositions[1][1] = roundedPositions[0][1];

			const allPoints = [];

			let diffPoints;

			if (direction === 'horizontal') {
				const lowerPosition = roundedPositions[0][1] < roundedPositions[1][1] ? roundedPositions[0] : roundedPositions[1];
				const higherPosition = roundedPositions[0][1] > roundedPositions[1][1] ? roundedPositions[0] : roundedPositions[1];

				diffPoints = Math.abs(higherPosition[1] - lowerPosition[1]);

				for (let i = 0; i <= diffPoints; i++) {
					allPoints.push([lowerPosition[0], lowerPosition[1] + i]);
				}
			}

			if (direction === 'vertical') {
				const lowerPosition = roundedPositions[0][0] < roundedPositions[1][0] ? roundedPositions[0] : roundedPositions[1];
				const higherPosition = roundedPositions[0][0] > roundedPositions[1][0] ? roundedPositions[0] : roundedPositions[1];

				diffPoints = Math.abs(higherPosition[0] - lowerPosition[0]);

				for (let i = 0; i <= diffPoints; i++) {
					allPoints.push([lowerPosition[0] + i, lowerPosition[1]]);
				}
			}

			return {
				...path,
				positions: roundedPositions,
				allPoints,
			};
		});
	}, [currentFloor?.paths]);

	useEffect(() => {
		if (selectedBooth && navigation) {
			const booth = currentFloorBooths.find((booth) => booth.linked === selectedBooth);
			const urlParams = new URLSearchParams(window.location.search);
			const urlFloor = urlParams.get('floor');
			const urlStart = urlParams.get('start');

			let lat;
			let lng;

			if (!urlFloor || !urlStart || Number(urlFloor) !== currentFloor?.floor) {
				lat = Number(currentFloor.defaultStartLat);
				lng = Number(currentFloor.defaultStartLng);
			}

			if (urlStart && Number(urlFloor) === currentFloor.floor) {
				const start = urlStart.split(',');
				lat = Number(start[0]);
				lng = Number(start[1]);
			}

			if (!lat || !lng) return;

			const start = [Math.round(lat), Math.round(lng)];
			// setStartIconPosition(start);
			const bounds = [
				[booth.bounds._northEast.lat, booth.bounds._northEast.lng],
				[booth.bounds._southWest.lat, booth.bounds._southWest.lng],
			];
			const center = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];
			const end = center;

			// iterate through all the paths on current floor, find the path that has a point that is closest to the start point
			let closestPathToStart = null;
			let closestDistanceToStart = null;
			let closestPointToStart = null;

			roundedPaths.forEach((path) => {
				path.allPoints.forEach((point) => {
					const distance = Math.sqrt(Math.pow(point[0] - start[0], 2) + Math.pow(point[1] - start[1], 2));
					if (!closestDistanceToStart || distance < closestDistanceToStart) {
						closestDistanceToStart = distance;
						closestPathToStart = path;
						closestPointToStart = point;
					}
				});
			});

			// iterate through all the paths on current floor, find the path that has a point that is closest to the end point
			// let closestPathToEnd = null;
			let closestDistanceToEnd = null;
			let closestPointToEnd = null;

			roundedPaths.forEach((path) => {
				path.allPoints.forEach((point) => {
					const distance = Math.sqrt(Math.pow(point[0] - end[0], 2) + Math.pow(point[1] - end[1], 2));
					if (!closestDistanceToEnd || distance < closestDistanceToEnd) {
						closestDistanceToEnd = distance;
						// closestPathToEnd = path;
						closestPointToEnd = point;
					}
				});
			});


			// console.log(closestPathToStart);
			// console.log(closestPointToStart);
			setStartPoint(closestPointToStart);
			setEndPoint(closestPointToEnd);

			// find all intersections between all rounded paths, do not repeat
			const intersections = [];
			roundedPaths.forEach((path1) => {
				roundedPaths.forEach((path2) => {
					if (path1.id !== path2.id) {
						path1.allPoints.forEach((point1) => {
							path2.allPoints.forEach((point2) => {
								if (point1[0] === point2[0] && point1[1] === point2[1]) {
									intersections.push(point1);
								}
							});
						});
					}
				});
			});

			// remove duplicates from intersections
			const uniqueIntersections = [];
			intersections.forEach((intersection) => {
				const hasIntersection = uniqueIntersections.find((uniqueIntersection) => {
					return uniqueIntersection[0] === intersection[0] && uniqueIntersection[1] === intersection[1];
				});
				if (!hasIntersection) uniqueIntersections.push(intersection);
			});

			// const allPossibleRoutesFromStartPointToEndPoint = [];
			let leastTurnsRoutes = [];

			let combos = 0;

			const traverseRoute = (currentPoint, currentPath, prevRoutes, usedPaths) => {
				if (combos > 1000) return;
				const points = [...prevRoutes, currentPoint];

				if (leastTurnsRoutes.length && points.length === leastTurnsRoutes[0].length) {
					return;
				}

				// check if end point is in current path
				const hasEndPoint = currentPath.allPoints.find((point) => {
					return point[0] === closestPointToEnd[0] && point[1] === closestPointToEnd[1];
				});

				if (hasEndPoint) {
					points.push(closestPointToEnd);

					if (leastTurnsRoutes.length === 0) {
						combos++;
						leastTurnsRoutes.push(points);
						return;
					}

					if (points.length === leastTurnsRoutes[0].length) {
						combos++;
						leastTurnsRoutes.push(points);
						return;
					}

					if (points.length < leastTurnsRoutes[0].length) {
						combos++;
						leastTurnsRoutes = [points];
						return;
					}

					return;
					/// combos++;
					// points.push(closestPointToEnd);
					// allPossibleRoutesFromStartPointToEndPoint.push(points);
					// return;
				}

				// find all intersections that are in current path
				const currentIntersections = [];
				uniqueIntersections.forEach((intersection) => {
					currentPath.allPoints.forEach((point) => {
						if (point[0] === intersection[0] && point[1] === intersection[1]) {
							currentIntersections.push(intersection);
						}
					});
				});

				// make new list of paths that excludes used paths
				const newRoundedPaths = roundedPaths.filter((path) => {
					return !usedPaths.includes(path.id);
				});

				// find all paths that are connected to current path
				newRoundedPaths.forEach((path) => {
					if (!usedPaths.includes(path.id)) {
						currentIntersections.forEach((intersection) => {
							path.allPoints.forEach((point) => {
								if (point[0] === intersection[0] && point[1] === intersection[1]) {
									traverseRoute(intersection, path, points, [...usedPaths, path.id]);
								}
							});
						});
					}
				});
			}

			traverseRoute(closestPointToStart, closestPathToStart, [], [closestPathToStart.id]);
			// console.log(allPossibleRoutesFromStartPointToEndPoint);

			// find shortest route from allPossibleRoutesFromStartPointToEndPoint
			let shortestRoute = null;
			let shortestRouteSteps = null;

			leastTurnsRoutes.forEach((route) => {
				let steps = 0;
				route.reduce((prevPoint, currentPoint) => {
					const diff1 = prevPoint[0] - currentPoint[0];
					const diff2 = prevPoint[1] - currentPoint[1];
					const sumDiff = Math.abs(diff1) + Math.abs(diff2);
					steps += sumDiff;
					return currentPoint;
				});

				if (!shortestRoute || steps < shortestRouteSteps) {
					shortestRoute = route;
					shortestRouteSteps = steps;
				}

				if (steps === shortestRouteSteps) {
					if (route.length < shortestRoute.length) {
						shortestRoute = route;
						shortestRouteSteps = steps;
					}
				}
			});

			setNavigationPath(shortestRoute);
		}
	}, [currentFloor, currentFloorBooths, navigation, roundedPaths, selectedBooth]);

	return (
		<div id='MapArea' className='MapArea'>
			<MapContainer
				center={[0, 0]}
				zoom={isPrint ? 1 : minZoom}
				maxZoom={isPrint ? 1 : 5}
				minZoom={isPrint ? 1 : minZoom}
				ref={map}
				maxBounds={bounds}
				crs={CRS.Simple}
			>

				<Pane name='map' style={{ zIndex: 100 }}>
					{bounds ? (
						<ImageOverlay url={currentMap.url} bounds={bounds} />
					) : null}
				</Pane>

				<Pane name='sections' style={{ zIndex: 200 }}>
					{currentFloorSections.map((section) => {
						if (section.layerType === 'rectangle') {
							const bounds = [
								[section.bounds._northEast.lat, section.bounds._northEast.lng],
								[section.bounds._southWest.lat, section.bounds._southWest.lng],
							];

							return (
								<Rectangle
									key={section.id}
									bounds={bounds}
									pathOptions={{
										fillColor: section.fillColor,
										fillOpacity: 0.5,
										weight: 0,
									}}
								/>
							)
						}

						if (section.layerType === 'polygon') {
							return (
								<Polygon
									key={section.id}
									positions={section.positions}
									pathOptions={{
										fillColor: section.fillColor,
										fillOpacity: 0.5,
										weight: 0,
									}}
								/>
							)
						}

						return null;
					})}
				</Pane>

				<Pane name='booths' style={{ zIndex: 300 }}>
					{currentFloorBooths.map((booth) => {
						const bounds = [
							[booth.bounds._northEast.lat, booth.bounds._northEast.lng],
							[booth.bounds._southWest.lat, booth.bounds._southWest.lng],
						];

						return (
							<FeatureWithText
								map={map.current}
								shape={booth.layerType}
								key={booth.id}
								id={booth.id}
								bounds={bounds}
								positions={booth.positions}
								exhibitor={booth.exhibitor}
								className='booth'
								colors={basicInfo.boothColor}
							/>
						)
					})}
				</Pane>

				{/* <Pane name='paths' style={{ zIndex: 700 }}>
					<LayerGroup>
						{currentFloor?.paths.map((path) => {
							if (path.layerType === 'polyline') {
								return (
									<Polyline
										key={path.id}
										positions={path.positions}
										pathOptions={{
											color: 'black',
											opacity: 0.1,
											weight: 3,
										}}
										interactive={true}
									/>
								)
							}
							return null;
						})}
					</LayerGroup>
				</Pane> */}

				{navigation ? (
					<Pane name='navigation' style={{ zIndex: 250 }}>
						{navigationPath ? (
							<Polyline
								// key={navigationPath.id}
								positions={navigationPath}
								pathOptions={{
									color: '#E86D00',
									opacity: 1,
									weight: 3,
								}}
								interactive={false}
							/>
						) : null}
					</Pane>
				) : null}

				{navigation && startPoint ? (
					<NavMarker center={startPoint} marker={markerStart} />
				) : null}

				{navigation && endPoint ? (
					<NavMarker center={endPoint} marker={markerStart} />
				) : null}


			</MapContainer>
		</div>
	)
}

export default MapArea;